import React, { Fragment, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Col, Row, Card, Form, Button, Spinner, Image, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

// import authentication components
import useSignIn from 'react-auth-kit/hooks/useSignIn';
//import refresh from './../../../oauthRefresh';

// import axios
import axios from 'axios';

// import media files
import Logo from 'assets/images/brand/logo/logo-icon.svg';

// import custom components
import PasswordStrengthMeter from 'components/elements/passwordstrength/PasswordStrengthMeter';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

const SignUp = () => {
	const { t } = useTranslation();
	const [password, setPassword] = useState('');
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [isButtonDisabled, setIsButtonDisabled] = useState(false);
	const [isLoading, setIsLoading] = useState(false); // State for loading spinner
	const [step, setStep] = useState(1); // State für den aktuellen Schritt
	// State from compare-plan
	const location = useLocation();
	// Den state aus der Navigation extrahieren
	const selectedPlan = location.state?.plan;

	// State of errors in the form
	const [errors, setErrors] = useState({
		username: '',
	});

	const oauthSignIn = useSignIn();
	const navigate = useNavigate();

	const [showPassword, setShowPassword] = useState(false);

	const handlePasswordToggle = () => {
		setShowPassword(!showPassword);
	};

	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [email, setEmail] = useState('');
	const [companyName, setCompanyName] = useState('');
	const [employees, setEmployees] = useState(1);
	const [plan, setPlan] = useState('');

	useEffect(() => {
		if (selectedPlan) {
			setPlan(selectedPlan);
		}
	}, [selectedPlan]);

	const Stepper = () => {
		return (
			<div className="my-4">
				<div className="d-flex justify-content-center">
					<div
						className={
							step === 1
								? 'bg-primary'
								: firstName && lastName
								? 'bg-light-success'
								: 'bg-light-secondary'
						}
						style={{
							width: '33%',
							height: '5px',
							cursor: firstName && lastName ? 'pointer' : 'not-allowed',
							marginRight: '5px',
						}}
						onClick={() => firstName && lastName && toStep(1)}
					></div>
					<div
						className={
							step === 2
								? 'bg-primary'
								: email && password
									? 'bg-light-success'
									: 'bg-light-secondary'
						}
						style={{
							width: '33%',
							height: '5px',
							cursor: firstName && lastName ? 'pointer' : 'not-allowed',
							marginRight: '5px',
						}}
						onClick={() => firstName && lastName && toStep(2)}
					></div>
					<div
						className={
							step === 3
								? 'bg-primary'
								: companyName && employees
									? 'bg-light-success'
									: 'bg-light-secondary'
						}
						style={{
							width: '33%',
							height: '5px',
							cursor: email && password ? 'pointer' : 'not-allowed',
						}}
						onClick={() => email && password && toStep(3)}
					></div>
				</div>
			</div>
		);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsButtonDisabled(true);
		setIsLoading(true); // Set loading to true

		const options = {
			headers: { 'Content-Type': 'application/json' },
		};
		const data = {
			tenant_name: companyName,
			tenant_employees: employees,
			user_first_name: firstName,
			user_last_name: lastName,
			user_username: email,
			user_password: password,
			terms_and_conditions: e.target.agbcheckbox.checked,
			plan: selectedPlan ? selectedPlan : ''
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		axios.post(`${baseURL}/users/register`, data, options)
			.then(() => {
				const data = {
					username: email,
					password: password,
				};
				handleShowNotification(setNotification, 'info', t('Welcome'), t('You have successfully registered. You will be automatically logged in now.') + ' ' + t('Please wait a moment while we log you in.'));
				setTimeout(() => {
					Login(data);
				}, 4500); // 4.5 seconds
			})
			.catch((error) => {
				console.error('Registration error:', error, error?.response);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte überprüfe deine eingegebenen Daten oder kontaktiere den HelpDesk.');
				setIsButtonDisabled(false);
				setIsLoading(false); // Stop loading
				if (error.response?.status === 400 && error.response?.data?.message?.includes("Username")) {
					const errorMessage = error.response.data.message;
					const newErrors = {
						username: errorMessage ? errorMessage : '',
					};
					setErrors(newErrors);
					toStep(2);
				} else {
					setErrors({
						username: '',
					});
				}
			});
	};

	const Login = async (e) => {
		// API-Call Options
		const options = {
		};
		const data = {
			username: e.username,
			password: e.password
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.post(`${baseURL}/auth/login`, data, options)
			.then(response => {
				oauthSignIn({
					auth: {
						token: response.data.token,
						type: 'Bearer',
					},
					userState: response.data.data,
					refresh: response.data.refresh
				});
				navigate('/dashboard/overview');
			})
			.catch((error) => {
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Wir konnten dich nicht anmelden. Bitte kontaktiere den HelpDesk.');
				setIsButtonDisabled(false);
				setIsLoading(false); // Stop loading
			});
	};

	const toStep = (tostep) => {
		setStep(tostep);
	};

	const nextStep = () => {
		setStep(step + 1);
	};

	const prevStep = () => {
		setStep(step - 1);
	};

	return (
		<Fragment>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<Row className="align-items-center justify-content-center g-0 min-vh-100">
				<Col lg={5} md={5} className="py-8 py-xl-0">
					<Card>
						{/* Form */}
						<Form onSubmit={handleSubmit}>
							{step === 1 && (
								<Fragment>
									<Card.Body className="p-6">
										<div className="mb-4">
											<Link to="/">
												<Image src={Logo} className="mb-4" alt="" />
											</Link>
											<h1 className="mb-1 fw-bold">{t('Start now for free')}</h1>
											<Stepper />
										</div>
										<Row className="mb-4">
											<p className="text-muted">
												{t('Please enter your personal information. This information is used to create your personal account.')}
											</p>
											<Col lg={12} md={12} className="mb-3">
												<Form.Label htmlFor="firstname">
													{t('First Name')} <span className="text-danger">*</span>
												</Form.Label>
												{/* First Name */}
												<Form.Control
													type="text"
													id="firstname"
													placeholder={t('Enter First Name')}
													autoComplete="given-name"
													required
													onChange={(e) => setFirstName(e.target.value)}
													value={firstName}

												/>
											</Col>
											<Col lg={12} md={12} className="mb-3">
												<Form.Label htmlFor="firstname">
													{t('Last Name')} <span className="text-danger">*</span>
												</Form.Label>
												{/* Last Name */}
												<Form.Control
													type="text"
													id="lastname"
													placeholder={t('Enter Last Name')}
													autoComplete="family-name"
													required
													onChange={(e) => setLastName(e.target.value)}
													value={lastName}

												/>
												</Col>
												</Row>
												<Row>
													<Col lg={12} md={12} className="mb-0 d-grid gap-2">
														<Button
															variant="secondary"
															onClick={nextStep}
															disabled={!firstName || !lastName}
														>
															{t('Next')}
														</Button>
													</Col>
												</Row>
												<hr className="my-4" />
												<div className="mt-4 text-center">
													<span>
														{t('Already have an account?')}{' '}
														<Link to="/authentication/sign-in" className="ms-1">
															{t('Sign-in here')} <i className="fe fe-arrow-right"></i>
														</Link>
													</span>
												</div>
											</Card.Body>
										</Fragment>
									)}

							{step === 2 && (
								<Fragment>
									<Card.Body className="p-6">
										<div className="mb-4">
											<Link to="/">
												<Image src={Logo} className="mb-4" alt="" />
											</Link>
											<h1 className="mb-1 fw-bold">{t('Create access data')}</h1>
											<Stepper />
										</div>
										<Row className="mb-4">
											<p className="text-muted">
												{t('Please enter your email address and set a secure password.')}
											</p>
											<Col lg={12} md={12} className="mb-3">
												<Form.Label htmlFor="username">
													{t('Enter E-Mail')} <span className="text-danger">*</span>
												</Form.Label>
												{/* E-Mail */}
												<Form.Control
													type="email"
													id="username"
													placeholder={t('Enter Email')}
													autoComplete="username"
													required
													onChange={(e) => setEmail(e.target.value)}
													value={email}
													isInvalid={!!errors.username}
												/>
												<Form.Control.Feedback type="invalid">
													{t(errors.username)}
												</Form.Control.Feedback>
											</Col>
											<Col lg={12} md={12} className="mb-3">
												{/* New password */}
												<Form.Group className="mb-3">
													<Form.Label htmlFor="password">
														{t('Set New Password')} <span className="text-danger">*</span>
													</Form.Label>
													<div className="input-group">
														<Form.Control
															type={showPassword ? 'text' : 'password'}
															id="password"
															placeholder={t('Enter New Password')}
															autoComplete="new-password"
															value={password}
															onChange={(e) => setPassword(e.target.value)}
															required
														/>
														<span className="input-group-text" onClick={handlePasswordToggle}>
															<i className={`fe ${showPassword ? 'fe-eye-off' : 'fe-eye'}`}></i>
														</span>
													</div>
												</Form.Group>
												{/* Password strength meter */}
												{password.length > 0 ? (
													<>
														<Row className="align-items-center g-0">
															<Col sm={6}>
																<span
																	data-bs-toggle="tooltip"
																	data-placement="right"
																	title=""
																>
																	{t('Password strength')}{' '}
																	<OverlayTrigger
																		key="top"
																		placement="top"
																		overlay={
																			<Tooltip id="tooltip-top">
																				{t('We will help you to set a sufficiently secure password. For the best security, your password should consist of at least 6 characters, a capital letter and a number.')}
																			</Tooltip>
																		}
																	>
																		<i className="fas fa-question-circle ms-1"></i>
																	</OverlayTrigger>
																</span>
															</Col>
														</Row>
														<PasswordStrengthMeter password={password} />
													</>
												) : null}
											</Col>
										</Row>
										<Row>
											<Col lg={12} md={12} className="mb-0 d-grid gap-2">
												<Button
													variant="secondary"
													onClick={nextStep}
													disabled={!email || !password}
												>
													{t('Next')}
												</Button>
											</Col>
										</Row>
										<hr className="my-4" />
										<div className="mt-4 text-center">
											<span>
												{t('Do you have any questions?')}{' '}
												<Link to="/marketing/help-center" className="ms-1" target="_blank">
													{t('Open Help Center')} <i className="fe fe-arrow-up-right"></i>
												</Link>
											</span>
										</div>
									</Card.Body>
								</Fragment>
							)}

							{step === 3 && (
								<Fragment>
									<Card.Body className="p-6">
										<div className="mb-4">
											<Link to="/">
												<Image src={Logo} className="mb-4" alt="" />
											</Link>
											<h1 className="mb-1 fw-bold">{t('Provide company information')}</h1>
											<Stepper />
										</div>
										<Row className="mb-4">
											<p className="text-muted">
												{t('Finally, please provide us with information about your company.')}
											</p>
											<Col lg={12} md={12} className="mb-3">
												{/* Company Name */}
												<Form.Label>
													{t('Company Information')} <span className="text-danger">*</span>
												</Form.Label>
												<Form.Control
													type="text"
													id="companyname"
													placeholder={t('Enter Company Name')}
													autoComplete="organization"
													required
													onChange={(e) => setCompanyName(e.target.value)}
													value={companyName}
												/>
											</Col>
											<Col lg={12} md={12} className="mb-3">
												{/* Business Employees */}
												<Form.Label>{t('Number of employees in the company')}</Form.Label>
												<OverlayTrigger
													key="top"
													placement="top"
													overlay={
														<Tooltip id="tooltip-top">
															{t('This information has no influence on your usage options or costs, but is used purely for statistical purposes.')}
														</Tooltip>
													}
												>
													<i className="fas fa-question-circle ms-1"></i>
												</OverlayTrigger>
												<Form.Range
													min="1"
													max="3"
													steo="1"
													id="employees"
													required
													value={employees}
													onChange={(e) => setEmployees(e.target.value)}

												/>
												<Form.Text id="" muted>
													<Row>
														<Col lg={4} md={4} className="mb-3 text-start">
															1-10
														</Col>
														<Col lg={4} md={4} className="mb-3 text-center">
															10-300
														</Col>
														<Col lg={4} md={4} className="mb-3 text-end">
															ab 300
														</Col>
													</Row>
												</Form.Text>
											</Col>
										</Row>
										<Row className="mb-4">
											<Col lg={12} md={12} className="mb-3">
												{/* Checkbox */}
												<Form.Check type="checkbox" id="agbcheckbox">
													<Form.Check.Input type="checkbox" required />
													<Form.Check.Label>
														{t('I accept the')} {' '}
														<Link target='_blank' to="/marketing/about/terms-and-conditions/">
															{t('GTC')}
														</Link>{' '}
														{t('and')} {' '}
														<Link target='_blank' to="/marketing/about/privacy/">
															{t('Privacy Policy')}.
														</Link>
														<span className="text-danger">*</span>
													</Form.Check.Label>
												</Form.Check>
											</Col>
											<Col lg={12} md={12} className="mb-0 d-grid gap-2">
												{/* Button */}
												<Button variant="primary" type="submit" disabled={isButtonDisabled}>
													{isLoading ? (
														<>
															<Spinner animation="border" size="sm" className="me-2" />
															{t('Start now for free')}
														</>
													) : (
															t('Start now for free')
													)}
												</Button>
											</Col>
										</Row>
										<hr className="my-4" />
										<div className="mt-4 text-center">
											<span>
												{t('Having problems with your access?')}{' '}
												<Link to="/marketing/help-center" className="ms-1" target="_blank">
													{t('Open Help Center')} <i className="fe fe-arrow-up-right"></i>
												</Link>
											</span>
										</div>
									</Card.Body>
								</Fragment>
							)}
						</Form>
					</Card>
				</Col>
			</Row>
		</Fragment>
	);
};

export default SignUp;